import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { RenewApp } from "experian-renew-frontend-components"; // NOSONAR
import { BehaviorSubject, Observable, from, switchMap } from "rxjs";

import { environment } from "../../../../../environments/environment";
import { AuthoritiesEnum } from "../../../../shared/enums/authorities.enum";
import { Utils } from "../../../../shared/utils/utils";
import * as PrivacyActions from "../../privacy/state/actions/privacy.actions";
import { RenewAppResponse } from "../interfaces/renew-app-response.interface";
import { RenewAppToken } from "../interfaces/renew-app-token.interface";
import { AnalyticsService } from "@serasa/shared/services/analytics.service";
import { SiteSection } from "@serasa/shared/enums/site-section.enum";
import { UserService } from "@serasa/core/modules/users/services/user.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  public loggedIn$ = new BehaviorSubject(false);
  public accessToken$ = new BehaviorSubject("");

  public termIsAccepted = false;
  public isLoadingTerms = false;

  private _RenewAppData!: RenewAppToken;

  constructor(
    private readonly _router: Router,
    private readonly _http: HttpClient,
    private readonly _analyticsService: AnalyticsService,
    private readonly store: Store,
    private readonly _userService: UserService
  ) { }

  public callRenewApp(minitoken: string): void {
    this._RenewAppData = {
      env: environment.env,
      clientId: environment.clientId,
      minitoken,
    };

    const promiseToObservableRewApp$: Observable<RenewAppResponse> = from(
      RenewApp.init(this._RenewAppData),
    ) as Observable<RenewAppResponse>;

    promiseToObservableRewApp$
      .pipe(
        switchMap((response: RenewAppResponse) => {
          if (response?.success) {
            this._analyticsService.doPageLoad(SiteSection.PORTAL_LOGONS);
            this.updateAccessToken(response?.data?.accessToken || "");
          } else {
            this._router.navigate(["forbidden"]);
          }
          return this.sync();
        }),
      )
      .subscribe({
        next: () => {},
        error: (error: HttpErrorResponse) => { },
      });
  }

  public updateAccessToken(accessToken: string) {
    const isAllowed = Utils.checkTokenRole(
      accessToken,
      AuthoritiesEnum.ROLE_DT_LOGONPORTALVC_ADMIN,
    );

    if (isAllowed) {
      this._userService.invalidateLogonsCache();

      this.accessToken$.next(accessToken);
      Utils.setCookie("accessToken", accessToken);

      this.loggedIn$.next(true);

      this.store.dispatch(PrivacyActions.loadTerms());
      this.isLoadingTerms = true;

      this._router.navigate(["dashboard"]);
    } else {
      this._router.navigate(["forbidden"]);
    }
  }

  public isAuthenticated(): boolean {
    return this.loggedIn$.value;
  }

  public sync() {
    const headers = Utils.generateDefaultHeader();

    return this._http.post<unknown>(
      `${environment.logonPortalBaseUrl}/my-logons/synchronization`,
      {},
      {
        headers,
      },
    );
  }
}
